import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "San Francisco", cities: "Mission District, Castro, SOMA, Pacific Heights, Richmond" },
    { zone: "Daly City & Colma", cities: "Daly City, South San Francisco, Colma, Brisbane" },
    { zone: "Peninsula Norte", cities: "Burlingame, San Bruno, Millbrae, San Mateo, Pacifica" },
    { zone: "Condado de Marin", cities: "San Rafael, Novato, Mill Valley, Sausalito, Tiburon" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Área de San Francisco</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en toda el Área de la Bahía de San Francisco.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en San Francisco</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const SanFranciscoSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "san-francisco", name: "San Francisco", type: "region", regionSlug: "san-francisco" }}>
      <SEO
        title="Limpieza del Hogar en San Francisco, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en San Francisco, Daly City, Burlingame, San Mateo y más. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar san francisco, servicio de limpieza sf ca, limpieza profunda san francisco, empleada doméstica san francisco, maid service bay area español, limpieza de casas daly city, limpieza burlingame san mateo, limpieza marin county"
        url="https://uplevelcleaning.com/es/san-francisco"
        locationName="San Francisco"
        regionSlug="san-francisco"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "San Francisco", url: "/es/san-francisco" }
        ]}
        reviews={[
          { author: "Valentina C.", rating: 5, reviewBody: "Vivo en el Mission District y encontrar un servicio de limpieza confiable era difícil. ¡Uplevel Cleaning cambió todo eso! Puntuales, detallistas y muy amables.", datePublished: "2026-03-18" },
          { author: "Andrés P.", rating: 5, reviewBody: "Contraté la limpieza profunda para mi apartamento en Pacific Heights y quedé impresionado. El equipo fue muy profesional y la atención al detalle fue increíble.", datePublished: "2026-02-22" },
          { author: "Daniela F.", rating: 5, reviewBody: "El servicio quincenal es perfecto para mi ritmo de vida en SF. Siempre regresan a una casa impecable. ¡100% recomendado!", datePublished: "2026-01-30" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="San Francisco" regionSlug="san-francisco" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en San Francisco",
                "Bay Area & Condado de Marin",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="San Francisco" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="san-francisco" />
          <Guarantee />
          <SpanishFAQ cityName="San Francisco" />
          <InternalLinks />
          <SpanishFinalCTA cityName="San Francisco" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default SanFranciscoSpanishPage;
